import React, { useState } from "react";

import { useDispatch } from "react-redux";

import type { BusbudError } from "@busbud/busbud-errors";
import CircularProgress from "@busbud/design-system-components/dist/CircularProgress/CircularProgress";
import Link from "@busbud/design-system-components/dist/Link";
import { Alert, Button, Heading } from "@busbud/horizon";

import { BrandLogo } from "@app/components/brand-logo/brandlogo";
import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";
import type { SigningMethod } from "@app/types/tracking/account";

import { SignInForm } from "./sign-in-form";
import { getErrorText } from "../../../../modules/account/utils";
import { logUserIn } from "../../../../modules/results/actions";
import { useAnimation } from "../authentication.styles";
import { OrChip } from "../or-chip";
import { useStyles } from "../sign-in-up.styles";
import { SocialConnectButtons } from "../social-buttons/social-connect-buttons";

interface Props {
  goToForgotPassword: () => void;
  goToSignUp: (email?: string) => void;
  goToResendAccountVerification: (email: string) => void;
  redirect_url?: string;
}

export const SignIn: React.FC<Props> = ({
  goToForgotPassword,
  goToSignUp,
  goToResendAccountVerification,
  redirect_url
}) => {
  const { classes: animation_classes } = useAnimation();
  const { translator, features } = useAppContext();
  const display_social_login = !!features.AUTHENTICATION_SOCIAL_LOGINS;
  const dispatch = useDispatch();
  const { cx, classes } = useStyles();
  const [error, setError] = useState<React.ReactNode>();
  const [isInProgress, setInProgress] = useState(false);
  const [is_form_shown, showEmailSignInForm] = useState(!display_social_login);

  const onSuccess = (user: NapiUser, sign_in_method?: SigningMethod) => {
    dispatch(logUserIn({ user, redirect_url, sign_in_method }));
  };

  const onError = (error: BusbudError) => {
    setError(getErrorText(translator, error.name, error));
  };

  return (
    <div
      className={cx(
        "relative flex flex-col gap-250",
        animation_classes.container
      )}
    >
      <div className="flex flex-col gap-100">
        <div className="self-center">
          <BrandLogo variant="avatar" size="sm" />
        </div>

        <Heading size="lg" as="h2" className="text-center font-weight-bolder">
          {translator.t("!account.signin.title")}
        </Heading>
      </div>

      <div className="mt-200 flex flex-col gap-200">
        {
          // this wrapping div is necessary to avoid re-render from scratch of the SignInForm
        }
        {!!error && (
          <Alert
            severity="negative"
            hasRoleAlert
            className={!error ? "sr-only" : undefined}
          >
            <Alert.Description>{error}</Alert.Description>
          </Alert>
        )}
        {!!isInProgress && (
          <div className={classes.backdrop}>
            <CircularProgress size="lg" />
          </div>
        )}
        {!!display_social_login && (
          <>
            <SocialConnectButtons
              setInProgress={setInProgress}
              onSuccess={onSuccess}
              onError={onError}
            />
            <OrChip />
          </>
        )}
        <Button
          variant="contained"
          size="lg"
          onClick={() => showEmailSignInForm(true)}
          className={cx({
            hidden: is_form_shown
          })}
        >
          {translator.t("!account.authentication.email")}
        </Button>
        {!!is_form_shown && (
          <>
            <SignInForm
              setInProgress={setInProgress}
              setError={setError}
              onSuccess={onSuccess}
              goToSignUp={goToSignUp}
              goToResendAccountVerification={goToResendAccountVerification}
            />
            <Link
              size="lg"
              align="center"
              fontWeight="bold"
              onClick={goToForgotPassword}
            >
              {translator.t("!account.signin.forgot-password")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
